import type { Degree, Distance, Point, Rectangle, SpatialRectangle } from "@pdfking/math"
import { AddMargins, BoundingBox, GetXY, Outline, Subtract } from "@pdfking/math"
import { HALF_TURN, QUARTER_TURN, ZERO_DEGREE } from "@pdfking/math/constants"
import { MM } from "@pdfking/quantity"
import { Constant, DeleteDuplicates, SortBy } from "@pdfking/tfunc"

import {
  CROSSMARK,
  CROSSMARK_OFFSET,
  CROSSMARK_SPACE,
  FOLD_CROSSMARK,
  FOLD_CROSSMARK_SIZE,
  FOLDMARK,
  FOLDMARK_STEP,
} from "#constants"
import type { Drawable, ImpositionNode } from "#internal"

import { CreateGroup } from "../../../shapes/CreateGroup"
import { DistanceRound } from "../../../utils/DistanceRound"
import { GrowGuides } from "../../common/grow/GrowGuides"
import { PlotWithSpace } from "../../common/layout/PlotWithSpace"

/** The turn that points a mark's x down the sheet. */
const DOWN = Subtract(ZERO_DEGREE, QUARTER_TURN)

/** Every outermost grow under `node`: its pages are all its own. */
function* Grows(node: ImpositionNode): Generator<ImpositionNode> {
  if (node.data?.grow) {
    yield node
    return
  }
  for (const child of node.children) yield* Grows(child)
}

type Axis = keyof Point<unknown>

// A box's span along `axis`: where it starts and where it ends.
const span = (box: SpatialRectangle<Distance>, axis: Axis): [Distance, Distance] =>
  axis === "x"
    ? [box.x, (box.x + box.width) as Distance]
    : [box.y, (box.y + box.height) as Distance]

// A line between two runs of pages along an axis: where the one before ends,
// where the one after starts, and halfway, where it runs.
type Line = { before: Distance; after: Distance; at: Distance }

// Every line between a run of pages along `axis` and the next.
const between = (pages: SpatialRectangle<Distance>[], axis: Axis): Line[] => {
  const spans = SortBy(
    DeleteDuplicates(
      pages.map(page => span(page, axis)),
      ([start, end]) => `${DistanceRound(start)}:${DistanceRound(end)}`
    ),
    ([start]) => DistanceRound(start)
  )
  return spans.slice(1).flatMap(([after], i) => {
    const [, before] = spans[i]!
    return DistanceRound(after) >= DistanceRound(before)
      ? [{ before, after, at: ((before + after) / 2) as Distance }]
      : []
  })
}

/**
 * The fold marks of one book signature `grow`, landed as `landed`: a
 * {@link FOLD_CROSSMARK} wherever two of its folds — the lines between its
 * pages — cross, and where each fold leaves it, two {@link FOLDMARK}s turned
 * to face it, one either side of its edge, reading as four even dashes
 * centred on it. A fold its pages share an edge on is left to the marks round each
 * page. Each where it has room on `node`.
 */
function* FoldMarks(node: ImpositionNode, landed: NonNullable<ReturnType<typeof GrowGuides>>) {
  const pages = landed.cells.map(cell => cell.box)
  const half = (FOLD_CROSSMARK_SIZE / 2) as Distance
  const xs = between(pages, "x").map(line => line.at)
  const ys = between(pages, "y").map(line => line.at)
  for (const x of xs)
    for (const y of ys)
      yield* PlotWithSpace(
        node,
        {
          x: (x - half) as Distance,
          y: (y - half) as Distance,
          width: FOLD_CROSSMARK_SIZE,
          height: FOLD_CROSSMARK_SIZE,
        },
        Constant(FOLD_CROSSMARK)
      )

  const shared = (folds: Distance[]) => {
    const set = new Set(folds.map(DistanceRound))
    return (at: Distance) => set.has(DistanceRound(at))
  }
  const shared_x = shared(landed.folds.x)
  const shared_y = shared(landed.folds.y)
  const edge = GetXY(Outline(pages))
  // The two foldmarks' middles, either side of an edge.
  const marks = (at: number) => [at - FOLDMARK_STEP / 2, at + FOLDMARK_STEP / 2] as Distance[]
  // The room a foldmark keeps: its 3mm along the fold, a crossmark's room
  // either side of it — standing up along an x fold, lying down along a y one.
  const across = (CROSSMARK_SPACE * 2) as Distance
  const upright: Rectangle<Distance> = { width: across, height: MM(3) }
  const flat: Rectangle<Distance> = { width: MM(3), height: across }
  const ends: Readonly<[Point<Distance>, Degree, Rectangle<Distance>]>[] = [
    ...xs
      .filter(x => !shared_x(x))
      .flatMap(x => [
        ...marks(edge.y2).map(y => [{ x, y }, DOWN, upright] as const),
        ...marks(edge.y1).map(y => [{ x, y }, QUARTER_TURN, upright] as const),
      ]),
    ...ys
      .filter(y => !shared_y(y))
      .flatMap(y => [
        ...marks(edge.x1).map(x => [{ x, y }, ZERO_DEGREE, flat] as const),
        ...marks(edge.x2).map(x => [{ x, y }, HALF_TURN, flat] as const),
      ]),
  ]
  for (const [middle, rotation, size] of ends)
    yield* PlotWithSpace(
      node,
      {
        x: (middle.x - size.width / 2) as Distance,
        y: (middle.y - size.height / 2) as Distance,
        ...size,
      },
      () => CreateGroup(FOLDMARK, { rotation })
    )
}

/**
 * The marks of a fill: one past every corner of every page of every grow under
 * it, `CROSSMARK_OFFSET` off the page whatever bleed is there — a
 * {@link FOLDMARK} on a line the grow folds on, a {@link CROSSMARK} on one it
 * is cut on — moved and turned onto its middle. None without `CROSSMARK_SPACE`
 * round its middle clear of every page — a grid neighbour's, or a taller one's
 * along its edge. A book signature's {@link FoldMarks} too.
 */
export const DrawFillCrossmarks: Drawable = function* (node, _frame, ctx) {
  if (!ctx.plot_crossmarks) return
  for (const grow of Grows(node)) {
    const data = grow.data?.grow
    const landed = data && GrowGuides(grow, data)
    if (!landed) continue
    if (data.grow.is_book) yield* FoldMarks(node, landed)
    const on_fold = (folds: Distance[]) => {
      const set = new Set(folds.map(DistanceRound))
      return (at: Distance) => set.has(DistanceRound(at))
    }
    const fold_x = on_fold(landed.folds.x)
    const fold_y = on_fold(landed.folds.y)
    for (const cell of landed.cells) {
      const { x1, x2, y1, y2 } = GetXY(cell.box)
      const top = (y2 + CROSSMARK_OFFSET) as Distance
      const bottom = (y1 - CROSSMARK_OFFSET) as Distance
      const left = (x1 - CROSSMARK_OFFSET) as Distance
      const right = (x2 + CROSSMARK_OFFSET) as Distance
      // The mark's middle, its turn to face the page, whether its line is a fold.
      const marks = [
        [{ x: x1, y: top }, DOWN, fold_x(x1)],
        [{ x: x2, y: top }, DOWN, fold_x(x2)],
        [{ x: x1, y: bottom }, QUARTER_TURN, fold_x(x1)],
        [{ x: x2, y: bottom }, QUARTER_TURN, fold_x(x2)],
        [{ x: left, y: y1 }, ZERO_DEGREE, fold_y(y1)],
        [{ x: left, y: y2 }, ZERO_DEGREE, fold_y(y2)],
        [{ x: right, y: y1 }, HALF_TURN, fold_y(y1)],
        [{ x: right, y: y2 }, HALF_TURN, fold_y(y2)],
      ] as [Point<Distance>, Degree, boolean][]
      for (const [middle, rotation, is_fold] of marks)
        yield* PlotWithSpace(node, AddMargins(BoundingBox(middle), CROSSMARK_SPACE), () =>
          CreateGroup(is_fold ? FOLDMARK : CROSSMARK, { rotation })
        )
    }
  }
}
